import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { managerFamilyId } from '@/auth/familyManager';
import { useMe } from '@/auth/useAuth';
import { api } from '@/lib/api';
import { TutorSectionTabs } from '@/components/tutor/TutorSectionTabs';
import { DEFAULT_TUTOR_NAME, TutorAvatar } from '@/components/tutor/TutorAvatar';
import { StudyTutorParentGuide } from './study/StudyTutorParentGuide';
import { StudyPurchasePage } from './StudyPurchasePage';

interface FamilyLearner {
  id: string;
  nickname: string;
}
type StudyView = 'records' | 'method' | 'access';

/** Parent Study uses the existing family, report and commerce APIs. */
export function StudyParentPage() {
  const me = useMe();
  const familyId = managerFamilyId(me.data);
  const [search, setSearch] = useSearchParams();
  const requested = search.get('view');
  const view: StudyView =
    requested === 'method' || requested === 'access'
      ? requested
      : requested === 'records'
        ? 'records'
        : search.has('kid_id') || sessionStorage.getItem('study-checkout-order')
          ? 'access'
          : 'records';
  const panelId = useId();
  const kids = useQuery({
    queryKey: ['family', familyId, 'kids'],
    queryFn: () => api<FamilyLearner[]>(`/families/${familyId}/kids`),
    enabled: !!familyId,
    retry: false,
  });
  const changeView = (next: StudyView) =>
    setSearch((current) => {
      const updated = new URLSearchParams(current);
      updated.set('view', next);
      return updated;
    });
  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-5" data-testid="parent-study">
      <header className="rounded-[32px] border border-brand-mint/30 bg-wash-mint p-5 sm:p-7">
        <Link to="/portal" className="text-sm font-bold text-ink-soft">
          Back to family Home
        </Link>
        <div className="mt-4 flex items-center gap-4">
          <TutorAvatar size="large" expression="friendly" animated={false} />
          <div>
            <p className="eyebrow eyebrow-mint">{DEFAULT_TUTOR_NAME} · AI Tutor</p>
            <h1 className="mt-2 text-[28px] font-extrabold text-ink sm:text-[36px]">
              Your family’s Study
            </h1>
          </div>
        </div>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-soft">
          See what your child tried, how the Tutor helped and what they could do independently.
        </p>
      </header>
      <TutorSectionTabs
        label="Parent Study categories"
        items={[
          { id: 'records', label: 'Learning records' },
          { id: 'method', label: 'Tutor method' },
          { id: 'access', label: 'Study access' },
        ]}
        value={view}
        onChange={changeView}
        panelId={panelId}
      />
      <div id={panelId} role="tabpanel" aria-labelledby={`${panelId}-tab-${view}`}>
        {view === 'method' && <StudyTutorParentGuide initiallyExpanded standalone />}
        {view === 'access' && <StudyPurchasePage />}
        {view === 'records' && (
          <section aria-label="Choose a child’s Study records" className="space-y-4">
            <div>
              <h2 className="text-[22px] font-bold text-ink">Choose your child</h2>
              <p className="mt-2 text-sm text-ink-soft">
                Each report keeps learning records, Tutor support and Study settings together.
              </p>
            </div>
            {kids.isLoading && <p role="status">Loading your family…</p>}
            {kids.isError && (
              <div className="card-base" role="alert">
                <p>Could not load your children.</p>
                <button className="btn-pill-secondary mt-3" onClick={() => void kids.refetch()}>
                  Try again
                </button>
              </div>
            )}
            {(!familyId || kids.data?.length === 0) && (
              <div className="card-base">
                <p>Add a child to see their Study records.</p>
                <Link to="/portal" className="btn-pill-secondary mt-3 inline-block">
                  Open family Home
                </Link>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {kids.data?.map((kid) => (
                <article key={kid.id} className="card-base">
                  <h3 className="text-xl font-bold text-ink">{kid.nickname}</h3>
                  <p className="mt-2 text-sm text-ink-soft">
                    Attempts, help used, independent checks and next steps.
                  </p>
                  <Link
                    to={`/portal/family/${encodeURIComponent(kid.id)}/study`}
                    className="btn-pill-primary mt-4 inline-block"
                  >
                    View {kid.nickname}’s Study
                  </Link>
                </article>
              ))}
            </div>
            <p className="text-xs text-slate2">
              Records show activity and available evidence. Completing work with help does not
              establish independent ability.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
